import { lazy } from "react";
import { HashRouter, Routes, Route } from "react-router-dom";
import { SuspenseWrapper } from "@/shared/components/feedback/SuspenseWrapper";
import { AuthGuard } from "@/shared/auth/AuthGuard";
import { AdminGuard } from "@/shared/auth/AdminGuard";
import { SessionProvider } from "@/shared/auth/SessionContext";
import { SessionGate } from "@/shared/auth/SessionGate";
import { AppLayout } from "@/shared/components/layout/AppLayout";
import { AdminLayout } from "@/shared/components/layout/AdminLayout";
import { appRoutes } from "./router";
import { adminRoutes } from "./adminRoutes";

const LoginPage = lazy(() => import("@/pages/LoginPage"));
const SignupPage = lazy(() => import("@/pages/SignupPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const SuspendedPage = lazy(() => import("@/pages/SuspendedPage"));
const InviteRedemptionPage = lazy(() => import("@/pages/InviteRedemptionPage"));
const NoWorkspacePage = lazy(() => import("@/pages/NoWorkspacePage"));

export function App() {
  return (
    <HashRouter>
      <Routes>
        {/* Public route */}
        <Route
          path="/login"
          element={
            <SuspenseWrapper>
              <LoginPage />
            </SuspenseWrapper>
          }
        />

        {/* Public route — self-service trial signup */}
        <Route
          path="/signup"
          element={
            <SuspenseWrapper>
              <SignupPage />
            </SuspenseWrapper>
          }
        />

        {/* Public route — forgot password */}
        <Route
          path="/forgot-password"
          element={
            <SuspenseWrapper>
              <ForgotPasswordPage />
            </SuspenseWrapper>
          }
        />

        {/* Public route — reset password (after clicking email link) */}
        <Route
          path="/reset-password"
          element={
            <SuspenseWrapper>
              <ResetPasswordPage />
            </SuspenseWrapper>
          }
        />

        {/* Public route — no workspace (OAuth user without invite) */}
        <Route
          path="/no-workspace"
          element={
            <SuspenseWrapper>
              <NoWorkspacePage />
            </SuspenseWrapper>
          }
        />

        {/* Public route — suspended tenant */}
        <Route
          path="/suspended"
          element={
            <SuspenseWrapper>
              <SuspendedPage />
            </SuspenseWrapper>
          }
        />

        {/* Public route — invite link redemption */}
        <Route
          path="/invite/:token"
          element={
            <SuspenseWrapper>
              <InviteRedemptionPage />
            </SuspenseWrapper>
          }
        />

        {/* Admin routes — isSystemAdmin only (SessionProvider for logout, no SessionGate) */}
        <Route element={<AdminGuard />}>
          <Route
            element={
              <SessionProvider>
                <AdminLayout />
              </SessionProvider>
            }
          >
            {adminRoutes.map((route) => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}
          </Route>
        </Route>

        {/* Protected tenant routes — session-gated */}
        <Route element={<AuthGuard />}>
          <Route
            element={
              <SessionProvider>
                <SessionGate>
                  <AppLayout />
                </SessionGate>
              </SessionProvider>
            }
          >
            {appRoutes.map((route) => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}
          </Route>
        </Route>
      </Routes>
    </HashRouter>
  );
}
